import { USER_API_URL } from '@repo/editor/src/api/constants';
import { getAccessToken } from '@repo/editor/src/api/user';
import { DEFAULT_TAKE } from '@repo/editor/src/global/constants';
import {
  GenericApiResponse,
  Post,
  TagType,
  TemplateUseCase,
  Template,
} from '@repo/editor/src/types';
import { fetchRetry, tryFetchRetry } from '@repo/editor/src/utils/fetchRetry';

import postServerApi from './postServerApi';

// eslint-disable-next-line @typescript-eslint/ban-types
type GenericResponse<T = {}> =
  | { success?: false; error: string }
  | ({ success: true } & T);

type PostResponse = GenericResponse<{ post: Post }>;
type TemplateResponse = GenericResponse<{ template: Template }>;

export interface PostsResponse {
  results: Post[];
  skip: number;
  take: number;
  sort?: string;
  useCase?: TemplateUseCase;
  tags?: TagType[];
  nextCursor?: string | null;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createAPI = ({ url }: { url: string }) => {
  const likePost = async ({
    postId,
  }: {
    postId: string;
  }): Promise<GenericResponse & { errorType?: string }> => {
    const response = await fetchRetry(`${url}/posts/like`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: encodeURI(`postId=${postId}`),
    });

    try {
      const responseJson = await response.json();

      if (!responseJson.success) {
        return {
          success: false,
          error: 'Failed to like the post.',
          errorType: responseJson.error,
        };
      }

      return { success: true };
    } catch (_error) {
      return { success: false, error: 'Failed to like the post.' };
    }
  };

  const unlikePost = async ({
    postId,
  }: {
    postId: string;
  }): Promise<GenericResponse & { errorType?: string }> => {
    const response = await fetchRetry(`${url}/posts/like/delete`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: encodeURI(`postId=${postId}`),
    });

    try {
      const responseJson = await response.json();

      if (!responseJson.success) {
        return {
          success: false,
          error: 'Failed to unlike the post.',
          errorType: responseJson.error,
        };
      }

      return { success: true };
    } catch (_error) {
      return { success: false, error: 'Failed to unlike the post.' };
    }
  };

  const upvotePost = async ({
    postId,
  }: {
    postId: string;
  }): Promise<GenericResponse> => {
    const response = await fetchRetry(`${url}/posts/upvote`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: encodeURI(`postId=${postId}`),
    });

    try {
      const responseJson = await response.json();

      if (!responseJson.success) {
        throw new Error('Failed to upvote the post.');
      }

      return { success: true };
    } catch (_error) {
      return { success: false, error: 'Failed to upvote the post.' };
    }
  };

  const removeUpvotePost = async ({
    postId,
  }: {
    postId: string;
  }): Promise<GenericResponse> => {
    const response = await fetchRetry(`${url}/posts/upvote/delete`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: encodeURI(`postId=${postId}`),
    });

    try {
      const responseJson = await response.json();

      if (!responseJson.success) {
        throw new Error('Failed to remove the upvote from the post.');
      }

      return { success: true };
    } catch (_error) {
      return {
        success: false,
        error: 'Failed to remove the upvote from the post.',
      };
    }
  };

  const fetchPost = async ({
    postId,
  }: {
    postId: string;
  }): Promise<PostResponse> => {
    const response = await fetchRetry(`${url}/posts/${postId}`, {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });

    const responseJson = await response.json();

    if ('id' in responseJson) {
      return { success: true, post: responseJson };
    }
    return responseJson;
  };

  const fetchTemplate = async ({
    templateId,
  }: {
    templateId: string;
  }): Promise<TemplateResponse> => {
    const response = await fetchRetry(`${url}/templates/${templateId}`, {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    });

    const responseJson = await response.json();

    if ('id' in responseJson) {
      return { success: true, template: responseJson };
    }
    return responseJson;
  };

  const getUserPosts = async (
    slug: string,
    take = 10,
    skip = 0
  ): Promise<PostsResponse> => {
    const resp = await fetchRetry(
      `${url}/posts/index/user/${slug}?take=${take}&skip=${skip}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );
    const result = await resp.json();

    return result;
  };

  const feed = async (
    take = DEFAULT_TAKE,
    cursor = null
  ): Promise<PostsResponse> => {
    const resp = await fetchRetry(
      `${url}/posts/feed/?skip=${cursor ? 1 : 0}&take=${take}${
        cursor ? `&cursor=${cursor}` : ''
      }`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );

    const result = await resp.json();
    return result;
  };

  const explore = ({
    useCaseSlug,
    take = DEFAULT_TAKE,
    skip = 0,
    sort = 'name',
    seed,
    tags = [],
  }: {
    useCaseSlug?: string;
    tags?: string[];
    take?: number;
    skip?: number;
    sort?: string;
    seed?: number;
  }): Promise<GenericApiResponse<PostsResponse>> => {
    return postServerApi.exploreFeed({
      useCaseSlug,
      skip,
      take,
      sort,
      seed,
      tags,
      token: getAccessToken(),
    });
  };

  const searchFeed = async ({
    query,
    take = DEFAULT_TAKE,
    skip = 0,
    sort,
    useCaseSlug,
    tags = [],
  }: {
    query: string;
    take?: number;
    skip?: number;
    sort?: string;
    useCaseSlug?: string;
    tags?: string[];
  }): Promise<GenericApiResponse<PostsResponse>> => {
    const params = new URLSearchParams({
      query,
      take: String(take),
      skip: String(skip),
    });

    if (sort) {
      params.append('sort', sort);
    }
    if (useCaseSlug) {
      params.append('useCaseSlug', useCaseSlug);
    }
    if (tags.length > 0) {
      params.append('tags', tags.join(','));
    }

    return await tryFetchRetry<GenericApiResponse<PostsResponse>>(
      `${url}/posts/search?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );
  };

  const getRelated = async (
    postId: string,
    take = DEFAULT_TAKE,
    skip = 0,
    // TODO: Remove with https://app.clickup.com/t/86c39t0gr
    semantic = false
  ): Promise<PostsResponse> => {
    const resp = await fetchRetry(
      `${url}/posts/${postId}/related?take=${take}&skip=${skip}${semantic ? '&semantic=true' : ''}`,
      {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );
    const response = await resp.json();

    return response;
  };

  const filter = async ({
    take = DEFAULT_TAKE,
    skip = 0,
    tag,
    ids,
    category,
    sort,
  }: {
    take?: number;
    skip?: number;
    tag?: string | null;
    ids?: string[] | null;
    category?: string | null;
    sort?: 'popularity' | 'trending' | 'recent' | 'name' | null;
  }): Promise<PostsResponse> => {
    const tagQuery = tag ? `&tag=${encodeURIComponent(tag.toLowerCase())}` : '';
    const idsQuery = ids?.length
      ? `&ids=${encodeURIComponent(ids.join(','))}`
      : '';
    const categoryQuery = category
      ? `&category=${encodeURIComponent(category)}`
      : '';
    const sortQuery = sort ? `&sort=${sort}` : '';
    const resp = await fetchRetry(
      `${url}/posts/filter?take=${take}&skip=${skip}${tagQuery}${idsQuery}${categoryQuery}${sortQuery}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );
    return await resp.json();
  };

  const deletePost = async (id: string): Promise<GenericResponse> => {
    const resp = await fetchRetry(`${url}/posts/delete`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: encodeURI(`id=${id}`),
    });
    const result = await resp.json();

    return result;
  };

  const getBookmarksAsPosts = async (
    take = 10,
    skip = 0,
    type = 'TEMPLATE'
  ): Promise<PostsResponse> => {
    const resp = await fetchRetry(
      `${url}/posts/bookmarks?take=${take}&skip=${skip}&bookmarkType=${type}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      }
    );
    const result = await resp.json();

    return result;
  };

  return {
    likePost,
    unlikePost,
    upvotePost,
    removeUpvotePost,
    fetchPost,
    fetchTemplate,
    getUserPosts,
    deletePost,
    feed,
    explore,
    getRelated,
    filter,
    getBookmarksAsPosts,
    searchFeed,
  };
};

export default createAPI({ url: USER_API_URL });
